import { useCallback, useRef, useState } from 'react'

import { FormattedMessage, useIntl } from 'react-intl'

import { htmlToText } from '@subaio/components'

import type { BrandFeatures } from 'apiTypes/BrandFeatures'

import { getDataToReview } from 'lib/getDataToReview'

import { ButtonGroup, ButtonSolid } from 'components/Buttons'
import { Checkbox } from 'components/Checkbox'
import { InputError } from 'components/FormFields/Shared'
import { Card } from 'components/Layout'

import type { Step, StepType } from '../../Step'
import type { StepHandlers } from '../../stepHandler'

type Props = TaggedType<Step, StepType.Terms> & {
	brandFeatures: Array<BrandFeatures>

	onNext: StepHandlers['onTermsAccept']
}

export const Terms = ({ brandFeatures, onNext }: Props) => {
	const [isConsentGiven, setIsConsentGiven] = useState(false)
	const [showInputError, setShowInputError] = useState(false)
	const { locale } = useIntl()
	const dataToReview = getDataToReview(brandFeatures)

	const decoupledRef = useRef<HTMLDivElement>()
	const ref = useCallback((element: HTMLDivElement | null) => {
		if (element) {
			decoupledRef.current = element.cloneNode(true) as HTMLDivElement
		} else {
			decoupledRef.current = undefined
		}
	}, [])

	return (
		<Card ref={ref}>
			<p>
				<FormattedMessage
					id='flow.terms.description1'
					defaultMessage='I consent to Subaio ApS collecting and processing my {dataToReview, select, expenses {payment information} income {income information} both {payment and income information} other {financial information}} from my selected accounts with Nordea and other banks for the purpose of creating an overview of my finances for my credit application with Nordea Danmark, Filial af Nordea Bank Abp, Finland (Nordea).'
					values={{ dataToReview }}
				/>
			</p>
			<p>
				<FormattedMessage
					id='flow.terms.description2'
					defaultMessage="Based on my {dataToReview, select, expenses {payment information} income {income information} both {payment and income information} other {financial information}}, Subaio provides a budget overview of my {dataToReview, select, expenses {expenses} income {income} both {expenses and income} other {finances}}, which is sent to Nordea without specifying payees. When Nordea receives my data, Nordea will process it in accordance with Nordea's Data Processing Policy. I can read more at <nordeaGdprLink></nordeaGdprLink>. Subaio is responsible for the collection and processing of my {dataToReview, select, expenses {payment data} income {income data} both {payment and income data} other {financial data}} and will delete my data 60 days after my budget overview has been sent to Nordea. I can read more about Subaio's processing of my data at <subaioPsd2Link></subaioPsd2Link>."
					values={{
						dataToReview,
						nordeaGdprLink: () => <a href='https://nordea.dk/gdpr'>nordea.dk/gdpr</a>,
						subaioPsd2Link: () =>
							locale.startsWith('da') ? (
								<a target='_blank' rel='noreferrer' href='https://subaio.com/psd2-privacy-policy-da'>
									subaio.com/psd2-privacy-policy-da
								</a>
							) : (
								<a target='_blank' rel='noreferrer' href='https://subaio.com/psd2-privacy-policy'>
									subaio.com/psd2-privacy-policy
								</a>
							),
					}}
				/>
			</p>
			<p>
				<FormattedMessage
					id='flow.terms.description3'
					defaultMessage='I have the option to withdraw my consent at any time by contacting Subaio.'
				/>
			</p>
			<Checkbox
				checkedType={isConsentGiven ? 'yes' : 'no'}
				hasError={showInputError}
				onClick={() => {
					setIsConsentGiven((isConsentGiven) => !isConsentGiven)
					setShowInputError(false)
				}}
			>
				<FormattedMessage
					id='flow.terms.labelConsent'
					defaultMessage='I have understood and agree to share the information with Nordea and Subaio'
				/>
			</Checkbox>
			{showInputError && (
				<InputError
					errorMessage={
						<FormattedMessage
							id='flow.terms.errorConsent'
							defaultMessage='You need to accept terms and conditions to proceed'
						/>
					}
					hasError
				/>
			)}
			<ButtonGroup>
				<ButtonSolid
					label={<FormattedMessage id='flow.terms.buttonAccept' defaultMessage='I accept' />}
					onClick={() => {
						if (!isConsentGiven) return setShowInputError(true)
						document.body.appendChild(decoupledRef.current!)
						const text = htmlToText(decoupledRef.current!)
						document.body.removeChild(decoupledRef.current!)
						return onNext(text)
					}}
				/>
			</ButtonGroup>
		</Card>
	)
}
